import React, { useState, useMemo } from 'react';
import {
    IconButton,
    Chip,
    Stack,
    Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommonTable from '../UI/Table/CommonTable';

/**
 * UserTable component for displaying and managing user data
 */
const UserTable = ({ 
    tableData = [], 
    handleEdit, 
    handleDelete, 
    isLoading = false, 
    actionButtons = null 
}) => {
    // State management
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        role: '',
        programCode: '',
        status: ''
    });

    // Memoized column definitions
    const columns = useMemo(() => [
        { key: 'serialNo', label: 'S.No', minWidth: 70, align: 'center' },
        { key: 'name', label: 'Name', minWidth: 120 },
        { key: 'email', label: 'Email', minWidth: 200 },
        { key: 'programCode', label: 'Program Code', minWidth: 120 },
        { key: 'role', label: 'Role', minWidth: 100 },
        { key: 'status', label: 'Status', minWidth: 100 },
        { key: 'actions', label: 'Actions', minWidth: 100 }
    ], []);

    // Helper functions
    const handleStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'success';
            case 'inactive': return 'error';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const confirmDelete = (email) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            handleDelete(email);
        }
    };

    const handleRowSelect = (user) => {
        setSelectedUser(selectedUser?.email === user.email ? null : user);
    };

    // Cell rendering
    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 500 }}>
                        {index + 1}
                    </Box>
                );
            case 'programCode':
                return (
                    <Chip 
                        label={value} 
                        size="small" 
                        sx={{
                            background: "#0a0068", 
                            color: "#fff", 
                            borderRadius: "6px",
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24
                        }}
                    />
                );
            case 'role':
                return (
                    <Chip 
                        label={value} 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                            fontWeight: 500, 
                            fontSize: '0.7rem', 
                            height: 22 
                        }}
                    />
                );
            case 'status':
                return (
                    <Chip
                        label={value}
                        size="small"
                        color={handleStatusColor(value)}
                        sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '0.7rem', 
                            height: 22 
                        }}
                    />
                );
            case 'actions':
                return (
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <EditIcon sx={{ fontSize: 18, color: '#666' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                confirmDelete(row.email);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 18, color: '#666' }} />
                        </IconButton>
                    </Stack>
                );
            default:
                return value;
        }
    };

    // Memoized data processing
    const filteredData = useMemo(() => {
        return tableData.filter(user => {
            // Search filter
            const searchMatch = Object.values(user).some(value => 
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            );

            // Active filters
            const roleMatch = !filters.role || user.role === filters.role;
            const programMatch = !filters.programCode || user.programCode === filters.programCode;
            const statusMatch = !filters.status || user.status === filters.status;

            return searchMatch && roleMatch && programMatch && statusMatch;
        });
    }, [tableData, searchTerm, filters]);

    // Memoized filter options
    const filterOptions = useMemo(() => ({
        role: [...new Set(tableData.map(user => user.role))],
        programCode: [...new Set(tableData.map(user => user.programCode))],
        status: [...new Set(tableData.map(user => user.status))]
    }), [tableData]);

    return (
        <CommonTable 
            columns={columns}
            data={filteredData}
            renderCustomCell={renderCell}
            onRowClick={handleRowSelect}
            selectedRow={selectedUser}
            actionButtons={actionButtons}
            isLoading={isLoading}
            onSearch={setSearchTerm}
            searchTerm={searchTerm}
            filters={{
                filters,
                setFilters,
                options: filterOptions
            }}
        />
    );
};

export default UserTable;